import { formatDate } from "utils/formatDate";
import { formatSize } from "utils/formatSize";
import { DATE_FORMATS, SPECIAL_NAMES } from "../../constants";
import { RootState } from "../../reducers";
// import {
//   acl as aclDraft,
//   bucketPolicy as bucketPolicyDraft,
//   corsPolicy as corsPolicyDraft,
//   lifecyclePolicy as lifecyclePolicyDraft
// } from "./drafts";
import {
  BUCKET_POLICY_TYPES,
  BUCKET_POLICY_TYPES_LABELS,
  BucketAcl,
  BucketCorsPolicy,
  BucketLifecyclePolicy,
  BucketLifecycleRule,
  BucketPolicy,
  BucketPolicyDocument,
  CorsPolicy,
  Credentials,
  S3Bucket,
  SubUser,
  SubUserKey,
  TableCredentials,
  TableS3Files,
  TableSubUser
} from "./types";
import { createSelector } from "reselect";

export const tableCredentialsListSelector = (
  state: RootState
): TableCredentials[] | null =>
  state.objectStorage.credentialsList?.map((credentials) => ({
    id: credentials.id,
    accessKey: credentials.access_key,
    accessSecret: credentials.access_secret,
    endpoint: credentials.endpoint,
    owner: credentials.owner
  })) || null;

export const credentialsListSelector = (
  state: RootState
): Credentials[] | null => state.objectStorage.credentialsList;

export const credentialsSelector = (state: RootState): Credentials | null =>
  state.objectStorage.credentials;

export const isCredentialsListLoadingSelector = (state: RootState): boolean =>
  state.objectStorage.isCredentialsListLoading;

export const areCredentialsLoadingSelector = (state: RootState): boolean =>
  state.objectStorage.areCredentialsLoading;

export const areCredentialsCreatingSelector = (state: RootState): boolean =>
  state.objectStorage.areCredentialsCreating;

export const areCredentialsRegeneratingSelector = (state: RootState): boolean =>
  state.objectStorage.areCredentialsRegenerating;

export const areCredentialsDeletingSelector = (state: RootState): boolean =>
  state.objectStorage.areCredentialsDeleting;

// export const tableBucketsListSelector = (
//   state: RootState
// ): TableS3Buckets[] | null =>
//   state.objectStorage.bucketsList?.map((bucket) => ({
//     id: bucket.name,
//     name: bucket.name,
//     visibility: bucket.visibility,
//     url: bucket.url,
//     creationDate: formatDate(
//       new Date(bucket.creation_date),
//       DATE_FORMATS.DATETIME
//     )
//   })) || null;

const bucketsListSelector = (state: RootState) =>
  state.objectStorage.bucketsList;
export const tableBucketsListSelector = createSelector(
  [bucketsListSelector],
  (bucketsList) => {
    if (!bucketsList) return null;
    return bucketsList.map((bucket) => ({
      id: bucket.name,
      name: bucket.name,
      visibility: bucket.visibility,
      url: bucket.url,
      is_versioning: bucket.is_versioning.toLowerCase(),
      creationDate: formatDate(
        new Date(bucket.creation_date),
        DATE_FORMATS.DATETIME
      )
    }));
  }
);

export const bucketSelector = (state: RootState): S3Bucket | null =>
  state.objectStorage.bucket
    ? {
        name: state.objectStorage.bucket.name,
        visibility: state.objectStorage.bucket.visibility,
        is_versioning: state.objectStorage.bucket.is_versioning.toLowerCase(),
        url:
          state.objectStorage.bucket.url === "public"
            ? `⚠️ ${state.objectStorage.bucket.url}`
            : state.objectStorage.bucket.url,
        creation_date: formatDate(
          new Date(state.objectStorage.bucket.creation_date),
          DATE_FORMATS.DATETIME
        )
      }
    : null;

export const bucketLifecycleRuleSelector = (
  state: RootState
): BucketLifecycleRule | null =>
  state.objectStorage.bucketLifecycleRule
    ? {
        id: state.objectStorage.bucketLifecycleRule.id,
        status: state.objectStorage.bucketLifecycleRule.status.toLowerCase(),
        days: state.objectStorage.bucketLifecycleRule.days
      }
    : null;
export const isBucketLifecycleRuleLoadingSelector = (
  state: RootState
): boolean => state.objectStorage.isBucketLifecycleRuleLoading;
export const isBucketLifecycleRuleSettingSelector = (
  state: RootState
): boolean => state.objectStorage.isBucketLifecycleRuleSetting;
export const isBucketLifecycleRuleDeletingSelector = (
  state: RootState
): boolean => state.objectStorage.isBucketLifecycleRuleDeleting;
export const isBucketCreatingSelector = (state: RootState): boolean =>
  state.objectStorage.isBucketCreating;

export const isBucketDeletingSelector = (state: RootState): boolean =>
  state.objectStorage.isBucketDeleting;

export const isVersioningStatusUpdatingSelector = (state: RootState): boolean =>
  state.objectStorage.isVersioningStatusUpdating;

export const isFileAllVersionsLoadingSelector = (state: RootState): boolean =>
  state.objectStorage.isFileAllVersionsLoading;

export const isFileAllVersionsDeletingSelector = (state: RootState): boolean =>
  state.objectStorage.isFileAllVersionsDeleting;

export const tableFileAllVersionsCountSelector = (
  state: RootState
): number | null => {
  const fileAllVersionsList = state.objectStorage.fileAllVersions?.versions;

  if (!fileAllVersionsList) return null;

  return fileAllVersionsList.length;
};

export const tableFlatFilesListSelector = (
  state: RootState
): TableS3Files[] | null => {
  const filesList = state.objectStorage.filesList?.objects;

  if (!filesList) return null;

  return filesList.map((file) => ({
    id: file.name,
    name: file.key,
    key: file.key,
    isFolder: file.is_folder,
    visibility: file.visibility,
    url: file.url,
    size: formatSize(Number(file.size)),
    lastModified: file.last_modified
      ? formatDate(new Date(file.last_modified), DATE_FORMATS.DATETIME)
      : ""
  }));
};

export const tableFilesListSelector = (
  state: RootState
): TableS3Files[] | null => {
  const filesList = state.objectStorage.filesList?.objects;

  if (!filesList) return null;

  return filesList.map((file) => {
    // Check if the file name is "../"
    if (file.name === SPECIAL_NAMES.FOLDER) {
      return {
        id: file.name,
        name: file.name,
        key: file.key,
        isFolder: file.is_folder,
        visibility: file.visibility,
        url: file.url,
        size: "",
        lastModified: ""
      };
    }

    // If the file name is not "../", include "size" and "lastModified"
    return {
      id: file.name,
      name: file.name,
      key: file.key,
      isFolder: file.is_folder,
      visibility: file.visibility,
      url: file.url,
      size: formatSize(Number(file.size)),
      lastModified: file.last_modified
        ? formatDate(new Date(file.last_modified), DATE_FORMATS.DATETIME)
        : ""
    };
  });
};

// export const filesTotalSelector = (state: RootState): number | null =>
//   state.objectStorage.filesList
//     ? state.objectStorage.filesList.total_keys
//     : null;

export const filesTotalSelector = (state: RootState): number | null => {
  if (state.objectStorage.filesList) {
    return state.objectStorage.filesList.total_keys || null;
  } else {
    return null;
  }
};

export const isFlatListSelector = (state: RootState): boolean | null =>
  state.objectStorage.filesList
    ? state.objectStorage.filesList.is_flat_list
    : null;

export const isFlatFilesListLoadingSelector = (state: RootState): boolean =>
  state.objectStorage.isFlatFilesListLoading;

export const isFlatFilesListByPrefixLoadingSelector = (
  state: RootState
): boolean => state.objectStorage.isFlatFilesListByPrefixLoading;

export const isFlatFilesListRefreshingSelector = (state: RootState): boolean =>
  state.objectStorage.isFlatFilesListRefreshing;

export const pageMapSelector = (
  state: RootState
): { [key: number]: string } | null => {
  return state.objectStorage.filesList
    ? state.objectStorage.filesList.page_map
    : null;
};

export const isNextPageAvailableSelector = (state: RootState): boolean =>
  state.objectStorage.filesList?.is_next_page_available || false;

export const isFileUploadingSelector = (state: RootState): boolean =>
  state.objectStorage.isFileUploading;

export const isFileDeletingSelector = (state: RootState): boolean =>
  state.objectStorage.isFileDeleting;

export const areFilesDeletingSelector = (state: RootState): boolean =>
  state.objectStorage.areFilesDeleting;

export const isFolderCreatingSelector = (state: RootState): boolean =>
  state.objectStorage.isFolderCreating;

export const ifFileSignedDownloadLinkGeneratingSelector = (
  state: RootState
): boolean => state.objectStorage.ifFileSignedDownloadLinkGenerating;

export const isFileDownloadingSelector = (state: RootState): boolean =>
  state.objectStorage.isFileDownloading;

export const uploadProgressSelector = (state: RootState): number =>
  state.objectStorage.uploadProgress;

export const isUploadSuccessfulSelector = (state: RootState): boolean =>
  state.objectStorage.isUploadSuccessful;

export const isBucketVisibilityChangingSelector = (state: RootState): boolean =>
  state.objectStorage.isBucketVisibilityChanging;

export const isCorsPolicyCheckingSelector = (state: RootState): boolean =>
  state.objectStorage.isCorsPolicyChecking;

export const isCorsPolicySettingSelector = (state: RootState): boolean =>
  state.objectStorage.isCorsPolicySetting;

export const corsPolicySelector = (state: RootState): CorsPolicy | null =>
  state.objectStorage.corsPolicy || null;

/*
  Bucket policies. Each policy comes from its own request, so each one gets its
  own selector.

  To fall back on the drafts, comment out the live selector, uncomment the draft
  one below it and uncomment the `./drafts` import at the top of the file. The
  draft variants keep the `bucket` guard so nothing is rendered before the
  bucket itself is there.
*/
export const bucketSourceSelector = (state: RootState): S3Bucket | null =>
  state.objectStorage.bucket;

export const bucketPolicySelector = (state: RootState): BucketPolicy | null =>
  state.objectStorage.bucketPolicy;
// export const bucketPolicySelector = createSelector(
//   [bucketSourceSelector],
//   (bucket): BucketPolicy | null => (bucket ? bucketPolicyDraft : null)
// );

export const bucketLifecyclePolicySelector = (
  state: RootState
): BucketLifecyclePolicy | null => state.objectStorage.bucketLifecyclePolicy;
// export const bucketLifecyclePolicySelector = createSelector(
//   [bucketSourceSelector],
//   (bucket): BucketLifecyclePolicy | null =>
//     bucket ? lifecyclePolicyDraft : null
// );

export const bucketCorsPolicySelector = (
  state: RootState
): BucketCorsPolicy | null => state.objectStorage.bucketCorsPolicy;
// export const bucketCorsPolicySelector = createSelector(
//   [bucketSourceSelector],
//   (bucket): BucketCorsPolicy | null => (bucket ? corsPolicyDraft : null)
// );

export const bucketAclSelector = (state: RootState): BucketAcl | null =>
  state.objectStorage.bucketAcl;
// export const bucketAclSelector = createSelector(
//   [bucketSourceSelector],
//   (bucket): BucketAcl | null => (bucket ? aclDraft : null)
// );

/*
  Subusers.
*/
export const subUsersListSelector = (state: RootState): SubUser[] | null =>
  state.objectStorage.subUsersList;

export const tableSubUsersListSelector = createSelector(
  [subUsersListSelector],
  (subUsers): TableSubUser[] | null =>
    subUsers?.map((subUser) => ({
      id: subUser.name,
      name: subUser.name,
      fullName: subUser.full_name,
      principalArn: subUser.principal_arn,
      access: subUser.access,
      keysCount: String(subUser.keys?.length ?? 0),
      keys:
        subUser.keys?.map((key) => ({
          id: key.access_key,
          accessKey: key.access_key,
          secretKey: key.secret_key
        })) || []
    })) || null
);

export const areSubUsersLoadingSelector = (state: RootState): boolean =>
  state.objectStorage.areSubUsersLoading;

export const isSubUserCreatingSelector = (state: RootState): boolean =>
  state.objectStorage.isSubUserCreating;

export const isSubUserDeletingSelector = (state: RootState): boolean =>
  state.objectStorage.isSubUserDeleting;

export const isSubUserKeyCreatingSelector = (state: RootState): boolean =>
  state.objectStorage.isSubUserKeyCreating;

export const isSubUserKeyDeletingSelector = (state: RootState): boolean =>
  state.objectStorage.isSubUserKeyDeleting;

export const subUserKeySelector = (state: RootState): SubUserKey | null =>
  state.objectStorage.subUserKey;

export const areBucketPoliciesLoadingSelector = (state: RootState): boolean =>
  state.objectStorage.isBucketPolicyLoading ||
  state.objectStorage.isBucketLifecyclePolicyLoading ||
  state.objectStorage.isBucketCorsPolicyLoading ||
  state.objectStorage.isBucketAclLoading;

const isPolicyEmpty = (policy: unknown): boolean =>
  !policy || typeof policy !== "object" || Object.keys(policy).length === 0;

// One entry per policy type, whether the bucket has that policy or not.
export const bucketPolicyDocumentsSelector = createSelector(
  [
    bucketPolicySelector,
    bucketLifecyclePolicySelector,
    bucketCorsPolicySelector,
    bucketAclSelector
  ],
  (policy, lifecyclePolicy, corsPolicy, acl): BucketPolicyDocument[] => {
    const policies: { [key in BUCKET_POLICY_TYPES]: unknown } = {
      [BUCKET_POLICY_TYPES.POLICY]: policy,
      [BUCKET_POLICY_TYPES.LIFECYCLE]: lifecyclePolicy,
      [BUCKET_POLICY_TYPES.CORS]: corsPolicy,
      [BUCKET_POLICY_TYPES.ACL]: acl
    };

    return Object.values(BUCKET_POLICY_TYPES).map((type) => {
      const isConfigured = !isPolicyEmpty(policies[type]);

      return {
        type,
        label: BUCKET_POLICY_TYPES_LABELS[type],
        isConfigured,
        json: isConfigured ? JSON.stringify(policies[type], null, 2) : ""
      };
    });
  }
);
